import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import {
  Menu,
  Badge,
  Icons,
  Sizes,
  Tag,
  NavBar,
  Pecten,
  Text,
} from '@sede-enterprise/shell-ds-react-framework';
import { IFullState } from '../types/All.type';
import { themeActions } from '../store/Theme';
import routepaths from '../router/routepaths';
import UserOptions from '../components/Auth/UserOptions';
import {
  ContentContainer,
  LayoutContainer,
  MidSection,
  NotificationDrawer,
  NavBarSection,
  GenesisHeader,
  PectenWrapper,
} from './style';
import './index.css';
import { getCurrentGasDay } from '../utils/common/utils';
import { getNominationCount } from '../store/reducers/Nominations';
import { getGeminiHealth, getActiveNotifications } from '../store/Notifications';
import SignalR from '../services/SignalR';
import RefreshGeminiData from './RefreshGeminiData';
import DashboardDirectLinks from '../components/Dashboard/DashboardDirectLinks';
import Notifications from '../components/Notifications';
import { sendNotification } from '../UI/Notification';
import { ERROR_MESSAGE } from '../constants/Gemini';
import Button from '../UI/Button';
import { EOSSwitchAppButton } from 'src/components/EOS/EOSLayout/style';
import { getEnv } from 'src/utils/env.vite';
import { rolePermissions } from 'src/constants/Permission';

const { MoonSolid, SunSolid, Bell: BellIcon, HomeSolid: HomeIcon } = Icons;

const Layout = () => {
  const { permissions } = useSelector((state: IFullState) => state.genesisUserRolePermission);
  const location = useLocation();
  const [confirmRefresh, setConfirmRefresh] = useState<boolean>(false);
  const { isDark } = useSelector((state: IFullState) => state.Theme);
  const { notificationCount, notificationList } = useSelector(
    (state: IFullState) => state.Notifications,
  );
  const { userName, mail, isEosUser } = useSelector(
    (state: IFullState) => state.genesisUserRolePermission,
  );
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState<string>(
    location.pathname.split('/')[1] ?? 'dashboard',
  );
  const currentGasDay = getCurrentGasDay().format('DD MMM YYYY');
  const { rejectedNominations } = useSelector((state: IFullState) => state.Nominations);
  const { isGeminiHealthy, errorGeminiHealth } = useSelector(
    (state: IFullState) => state.Notifications,
  );

  const showFieldNomsTab = permissions.includes(rolePermissions.listUkNomination);

  const getNominationCounts = () => {
    dispatch(getNominationCount());
  };

  const handleGetGeminiHealth = () => {
    dispatch(getGeminiHealth());
  };

  const handleGetActiveNotifications = () => {
    dispatch(getActiveNotifications());
  };

  useEffect(() => {
    handleGetGeminiHealth();
    handleGetActiveNotifications();
    getNominationCounts();
    const interval = setInterval(getNominationCounts, 1000 * 60 * 60); // every hour
    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    setCurrentTab(location.pathname.split('/')[1] || 'dashboard');
  }, [location]);

  useEffect(() => {
    if (errorGeminiHealth) {
      sendNotification({
        type: 'error',
        message: ERROR_MESSAGE,
      });
    }
  }, [errorGeminiHealth]);

  const handleDrawer = () => {
    setOpenDrawer(!openDrawer);
  };

  const tabs = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      route: '/dashboard',
    },
    {
      key: 'gemini',
      label: 'Gemini',
      route: '/gemini/gemini-balance',
    },
    {
      key: 'nominations',
      label: 'Nominations',
      route: '/nominations/nominations',
      itemIcon:
        rejectedNominations > 0 ? (
          <Badge
            title={`Rejected Nominations as of ${currentGasDay}.`}
            className='count'
            size={Sizes.Small}
            sentiment='negative'
          >
            {rejectedNominations}
          </Badge>
        ) : null,
    },
    {
      key: 'trade',
      label: 'Trading',
      route: '/trade/trading',
    },
    ...(showFieldNomsTab
      ? [
          {
            key: 'field-noms',
            label: 'Field Noms',
            route: '/field-noms/field-nominations',
          },
        ]
      : []),
    {
      key: 'cva',
      label: 'CVA',
      route: '/cva/cva-dashboard',
    },
    {
      key: 'summary-reports',
      label: 'Reports',
      route: '/summary-reports/reports',
    },
    {
      key: 'maintenance',
      label: 'System Admin',
      route: '/maintenance/activities',
    },
    {
      key: 'user-role',
      label: 'Roles & Management',
      route: '/user-role/users',
    },
    {
      key: 'audits',
      label: 'Audit',
      route: '/audits/audit',
    },
  ];

  const handleThemeOnChange = (value: boolean) => {
    dispatch(
      themeActions.updateTheme({
        isDark: value,
      }),
    );
  };

  const handleBackToLanding = () => {
    navigate('/');
  };

  const handleOnTabChange = (value: string) => {
    if (currentTab === value) {
      return;
    }
    const tab = tabs.find((item) => item.key === value);
    setCurrentTab(value);
    if (tab) {
      navigate(`${tab.route}`);
    }
  };

  const isLowerEnv =
    getEnv('VITE_APP_ENVIRONMENT') === 'Dev' || getEnv('VITE_APP_ENVIRONMENT') === 'Preprod';

  const onClickHeader = () => {
    if (isEosUser || isLowerEnv) {
      navigate(routepaths.landingPage);
    } else {
      setCurrentTab('dashboard');
      navigate(routepaths.dashboard);
    }
  };

  return (
    <LayoutContainer
      color={isDark ? 'rgba(255, 255, 255, 0.97)' : 'rgb(64, 64, 64)'}
      id='layout-container'
    >
      <NavBar
        pectenArea={
          <PectenWrapper onClick={onClickHeader}>
            <Pecten
              padding='minimum'
              size={Sizes.Medium}
              aria-label='Pecten Logo'
              title='Pecten Logo'
            />
            <GenesisHeader flexdirection='column'>
              <Text size='small'>Shell Energy</Text>
              <Text>
                <strong>Genesis</strong>
              </Text>
            </GenesisHeader>
          </PectenWrapper>
        }
        avatarProps={{
          title: userName,
          label: userName,
          secondaryLabel: mail,
          dropdownOverlay: <UserOptions />,
        }}
      >
        <NavBarSection>
          {(isEosUser || isLowerEnv) && (
            <EOSSwitchAppButton>
              <Button
                variant='transparent'
                onClick={handleBackToLanding}
                icon={<HomeIcon />}
                size='large'
                iconOnly
                className='eos-switch-button'
                title='Switch Application'
              />
            </EOSSwitchAppButton>
          )}
          <Menu
            mode='horizontal'
            emphasis
            items={tabs}
            activeKey={currentTab}
            size={Sizes.Large}
            selectedKeys={[currentTab]}
            onSelect={(selected) => handleOnTabChange(selected.key)}
            className='left-menu'
          />
          <div className='right-section'>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              Gemini:
              <Tag sentiment={`${isGeminiHealthy ? 'positive' : 'negative'}`}>
                {isGeminiHealthy ? 'Connected' : 'Disconnected'}
              </Tag>
            </div>
            <Button
              className='gemini-button'
              icon={
                <RefreshGeminiData
                  visible={confirmRefresh}
                  onPopupVisibleChange={setConfirmRefresh}
                />
              }
              iconOnly
              variant='transparent'
              title='Fetch Latest Gemini Data'
            />
            <Button
              className='theme-button'
              onClick={() => handleThemeOnChange(!isDark)}
              icon={isDark ? <SunSolid /> : <MoonSolid />}
              iconOnly
              variant='transparent'
              title={isDark ? 'Light theme' : 'Dark theme'}
            />
            <Button
              className='notifications-button'
              onClick={handleDrawer}
              icon={
                <>
                  <BellIcon />
                  {notificationCount > 0 && (
                    <Badge sentiment='negative' size={Sizes.Small} className='notification-count'>
                      {notificationCount}
                    </Badge>
                  )}
                </>
              }
              variant='transparent'
              iconOnly
              title='Notifications'
            />
          </div>
        </NavBarSection>
      </NavBar>
      <MidSection>
        <ContentContainer>
          <Outlet />
          <SignalR />
          {currentTab !== 'dashboard' && <DashboardDirectLinks />}
        </ContentContainer>
      </MidSection>
      <NotificationDrawer
        open={openDrawer}
        header='Notifications'
        onClose={handleDrawer}
        maskClosable={false}
        width={450}
      >
        <Notifications count={notificationCount} list={notificationList} />
      </NotificationDrawer>
    </LayoutContainer>
  );
};

export default Layout;
